/**
 * Partner applications, as the console sees them.
 *
 * These types mirror `service-person-app/src/types/index.ts` — the same records,
 * viewed from the reviewing side rather than the applicant's. When the API lands,
 * both apps read this shape from `GET /admin/applications`.
 */

import { portraitForId } from './assets';

export type ApplicationStatus =
  | 'draft'
  | 'submitted'
  | 'under_review'
  | 'interview_scheduled'
  | 'interview_done'
  | 'approved'
  | 'rejected'
  | 'suspended';

/** The stages ops moves an application through, in order. */
export const PIPELINE_STAGES = [
  'submitted',
  'under_review',
  'interview_scheduled',
  'interview_done',
] as const satisfies readonly ApplicationStatus[];

/**
 * Is this application still in ops' hands?
 *
 * `PIPELINE_STAGES.includes(status)` does not typecheck against the wider
 * `ApplicationStatus` union — `includes` narrows its argument to the tuple's own
 * members. This wraps the one cast so call sites stay readable.
 */
export function isOpen(status: ApplicationStatus): boolean {
  return (PIPELINE_STAGES as readonly ApplicationStatus[]).includes(status);
}

export const STATUS_LABELS: Record<ApplicationStatus, string> = {
  draft: 'Draft',
  submitted: 'New',
  under_review: 'Reviewing docs',
  interview_scheduled: 'Interview booked',
  interview_done: 'Awaiting decision',
  approved: 'Approved',
  rejected: 'Declined',
  suspended: 'Suspended',
};

export type DocumentKind =
  | 'id_proof'
  | 'address_proof'
  | 'profile_photo'
  | 'certificate'
  | 'police_verification';

export type DocumentReviewState = 'missing' | 'uploaded' | 'verified' | 'rejected';

export const DOCUMENT_LABELS: Record<DocumentKind, string> = {
  profile_photo: 'Profile photo',
  id_proof: 'Government ID',
  address_proof: 'Address proof',
  certificate: 'Training certificate',
  police_verification: 'Police verification',
};

/** Which documents block an approval if not verified. */
export const REQUIRED_DOCUMENTS: DocumentKind[] = ['profile_photo', 'id_proof', 'address_proof'];

export interface PartnerDocument {
  kind: DocumentKind;
  uri?: string;
  fileName?: string;
  size?: number;
  mimeType?: string;
  uploadedAt?: string;
  state: DocumentReviewState;
  reviewNote?: string;
}

export interface PersonalDetails {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: 'female' | 'male' | 'other' | 'prefer_not_to_say';
  email: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
}

export interface ProfessionalDetails {
  categoryIds: string[];
  specialties: string[];
  experienceYears: number;
  bio: string;
  travelRadiusKm: number;
  languages: string[];
  trainingInstitute?: string;
}

export interface BankDetails {
  accountHolderName: string;
  /** Stored masked in the console. Finance sees the full number, ops never does. */
  accountNumberLast4: string;
  ifsc: string;
  bankName: string;
  upiId?: string;
}

export type InterviewMode = 'video' | 'in_person' | 'phone';

export interface InterviewSlot {
  date: string;
  time: string;
  mode: InterviewMode;
  interviewerName: string;
  location?: string;
  notes?: string;
  rescheduleRequested?: boolean;
  rescheduleReason?: string;
}

/** Ops' notes on how the interview went, captured at decision time. */
export interface InterviewOutcome {
  attended: boolean;
  /** 1–5, on craft demonstrated. */
  skillScore: number;
  /** 1–5, on communication and professionalism. */
  conductScore: number;
  notes: string;
  recordedBy: string;
  recordedAt: string;
}

export interface ApplicationEvent {
  id: string;
  at: string;
  status: ApplicationStatus;
  title: string;
  detail?: string;
  /** Who caused it. Absent for applicant-driven events. */
  actor?: string;
}

export interface PartnerApplication {
  id: string;
  phone: string;
  status: ApplicationStatus;
  submittedAt?: string;
  updatedAt: string;
  personal: PersonalDetails;
  professional: ProfessionalDetails;
  bank?: BankDetails;
  documents: PartnerDocument[];
  interview?: InterviewSlot;
  outcome?: InterviewOutcome;
  decisionReason?: string;
  reapplyAfter?: string;
  /** Internal notes, never shown to the applicant. */
  internalNotes?: string;
  timeline: ApplicationEvent[];
  /** Set once approved and added to the therapist roster. */
  professionalId?: string;
  documentsReady?: boolean;
  pendingDocuments?: string[];
  canApprove?: boolean;
}

/* -------------------------------------------------------------------------- */
/* Seeds                                                                      */
/* -------------------------------------------------------------------------- */

const daysAgo = (d: number) => new Date(Date.now() - d * 86_400_000).toISOString();
const daysAhead = (d: number) => new Date(Date.now() + d * 86_400_000).toISOString();
const dateAhead = (d: number) => daysAhead(d).slice(0, 10);

function docs(
  overrides: Partial<Record<DocumentKind, Partial<PartnerDocument>>> = {},
): PartnerDocument[] {
  const base: DocumentKind[] = [
    'profile_photo',
    'id_proof',
    'address_proof',
    'certificate',
    'police_verification',
  ];

  return base.map((kind) => ({
    kind,
    state: 'uploaded',
    fileName: `${kind}.jpg`,
    mimeType: 'image/jpeg',
    size: 1_200_000,
    uploadedAt: daysAgo(3),
    ...overrides[kind],
  }));
}

function event(
  status: ApplicationStatus,
  title: string,
  at: string,
  detail?: string,
  actor?: string,
): ApplicationEvent {
  return {
    id: `evt_${Math.random().toString(36).slice(2, 9)}`,
    at,
    status,
    title,
    ...(detail ? { detail } : {}),
    ...(actor ? { actor } : {}),
  };
}

export const applicationsSeed: PartnerApplication[] = [
  /* ---- New, nobody has touched it yet ----------------------------------- */
  {
    id: 'app_2041',
    phone: '9822014455',
    status: 'submitted',
    submittedAt: daysAgo(0),
    updatedAt: daysAgo(0),
    personal: {
      firstName: 'Sanya',
      lastName: 'Bhatt',
      dateOfBirth: '1997-03-14',
      gender: 'female',
      email: 'sanya.bhatt@example.com',
      addressLine1: 'Rose Villa, Flat 12',
      addressLine2: 'Linking Road, Khar West',
      city: 'Mumbai',
      state: 'Maharashtra',
      pincode: '400052',
      emergencyContactName: 'Reena Bhatt',
      emergencyContactPhone: '9822011122',
    },
    professional: {
      categoryIds: ['cat_makeup', 'cat_hair'],
      specialties: ['Bridal', 'HD Makeup', 'Hair Styling'],
      experienceYears: 5,
      bio: 'Bridal artist trained at Lakmé Academy, five years across studio and freelance work.',
      travelRadiusKm: 15,
      languages: ['English', 'Hindi', 'Marathi'],
      trainingInstitute: 'Lakmé Academy, Bandra',
    },
    bank: {
      accountHolderName: 'Sanya Bhatt',
      accountNumberLast4: '8891',
      ifsc: 'HDFC0000521',
      bankName: 'HDFC Bank',
    },
    documents: docs(),
    timeline: [event('submitted', 'Application submitted', daysAgo(0))],
  },

  /* ---- Under review, one document rejected ------------------------------ */
  {
    id: 'app_2038',
    phone: '9713345566',
    status: 'under_review',
    submittedAt: daysAgo(2),
    updatedAt: daysAgo(1),
    personal: {
      firstName: 'Fatima',
      lastName: 'Shaikh',
      dateOfBirth: '1994-11-02',
      gender: 'female',
      email: 'fatima.shaikh@example.com',
      addressLine1: 'Green Acres, B-404',
      addressLine2: 'Aundh',
      city: 'Pune',
      state: 'Maharashtra',
      pincode: '411007',
      emergencyContactName: 'Imran Shaikh',
      emergencyContactPhone: '9713340001',
    },
    professional: {
      categoryIds: ['cat_skin'],
      specialties: ['Facial', 'Skin Therapy', 'Anti Aging'],
      experienceYears: 7,
      bio: 'Licensed skin therapist with a clinical background, focused on acne and pigmentation.',
      travelRadiusKm: 10,
      languages: ['English', 'Hindi'],
      trainingInstitute: 'VLCC Institute',
    },
    bank: {
      accountHolderName: 'Fatima Shaikh',
      accountNumberLast4: '2210',
      ifsc: 'ICIC0001188',
      bankName: 'ICICI Bank',
    },
    documents: docs({
      profile_photo: { state: 'verified' },
      id_proof: { state: 'verified' },
      address_proof: {
        state: 'rejected',
        reviewNote: 'Utility bill is 8 months old. Please send one from the last 3 months.',
      },
      police_verification: { state: 'missing', fileName: undefined, uploadedAt: undefined },
    }),
    internalNotes: 'Strong clinical background. Book her a counter slot for kits if approved.',
    timeline: [
      event('under_review', 'Address proof rejected', daysAgo(1), 'Older than 3 months', 'Operations'),
      event('under_review', 'Moved to review', daysAgo(2), undefined, 'Operations'),
      event('submitted', 'Application submitted', daysAgo(2)),
    ],
  },

  /* ---- Interview booked, applicant asked to move it --------------------- */
  {
    id: 'app_2032',
    phone: '9445567788',
    status: 'interview_scheduled',
    submittedAt: daysAgo(5),
    updatedAt: daysAgo(1),
    personal: {
      firstName: 'Divya',
      lastName: 'Menon',
      dateOfBirth: '1999-07-28',
      gender: 'female',
      email: 'divya.menon@example.com',
      addressLine1: 'Palm Grove, 7A',
      addressLine2: 'Indiranagar',
      city: 'Bengaluru',
      state: 'Karnataka',
      pincode: '560038',
      emergencyContactName: 'Lakshmi Menon',
      emergencyContactPhone: '9445560000',
    },
    professional: {
      categoryIds: ['cat_nails', 'cat_skin'],
      specialties: ['Gel Extensions', 'Manicure', 'Pedicure', 'Clean Up'],
      experienceYears: 4,
      bio: 'Nail artist known for clean gel work and long-lasting extensions.',
      travelRadiusKm: 20,
      languages: ['English', 'Malayalam', 'Kannada'],
    },
    bank: {
      accountHolderName: 'Divya Menon',
      accountNumberLast4: '4417',
      ifsc: 'SBIN0011234',
      bankName: 'State Bank of India',
    },
    documents: docs({
      profile_photo: { state: 'verified' },
      id_proof: { state: 'verified' },
      address_proof: { state: 'verified' },
      certificate: { state: 'verified' },
    }),
    interview: {
      date: dateAhead(2),
      time: '11:00',
      mode: 'video',
      interviewerName: 'Operations',
      location: 'https://meet.kayracare.com/partner-interview',
      notes: 'Keep your ID handy — we verify it on camera.',
      rescheduleRequested: true,
      rescheduleReason: 'I have a client booking that morning. Afternoon would work better.',
    },
    timeline: [
      event('interview_scheduled', 'Reschedule requested', daysAgo(1), 'Client booking clash'),
      event('interview_scheduled', 'Interview scheduled', daysAgo(3), `${dateAhead(2)} at 11:00`, 'Operations'),
      event('under_review', 'Documents verified', daysAgo(4), undefined, 'Operations'),
      event('submitted', 'Application submitted', daysAgo(5)),
    ],
  },

  /* ---- Interview done, decision pending -------------------------------- */
  {
    id: 'app_2027',
    phone: '9266778811',
    status: 'interview_done',
    submittedAt: daysAgo(9),
    updatedAt: daysAgo(1),
    personal: {
      firstName: 'Ritika',
      lastName: 'Chawla',
      dateOfBirth: '1992-01-19',
      gender: 'female',
      email: 'ritika.chawla@example.com',
      addressLine1: 'Sunrise Heights, 1102',
      addressLine2: 'Sector 45',
      city: 'Delhi NCR',
      state: 'Haryana',
      pincode: '122003',
      emergencyContactName: 'Vikas Chawla',
      emergencyContactPhone: '9266770000',
    },
    professional: {
      categoryIds: ['cat_hair'],
      specialties: ['Keratin', 'Hair Colour', 'Blowout'],
      experienceYears: 9,
      bio: 'Colour and keratin specialist, Schwarzkopf and L’Oréal Pro certified.',
      travelRadiusKm: 25,
      languages: ['English', 'Hindi'],
      trainingInstitute: 'Jawed Habib Academy',
    },
    bank: {
      accountHolderName: 'Ritika Chawla',
      accountNumberLast4: '9903',
      ifsc: 'AXIS0000876',
      bankName: 'Axis Bank',
    },
    documents: docs({
      profile_photo: { state: 'verified' },
      id_proof: { state: 'verified' },
      address_proof: { state: 'verified' },
      certificate: { state: 'verified' },
      police_verification: { state: 'verified' },
    }),
    interview: {
      date: daysAgo(1).slice(0, 10),
      time: '15:00',
      mode: 'video',
      interviewerName: 'Operations',
    },
    outcome: {
      attended: true,
      skillScore: 5,
      conductScore: 5,
      notes:
        'Excellent technical depth on colour correction. Articulate, clearly used to premium clients. Recommend approval.',
      recordedBy: 'Operations',
      recordedAt: daysAgo(1),
    },
    timeline: [
      event('interview_done', 'Interview completed', daysAgo(1), 'Skill 5/5, conduct 5/5', 'Operations'),
      event('interview_scheduled', 'Interview scheduled', daysAgo(4), undefined, 'Operations'),
      event('under_review', 'Documents verified', daysAgo(7), undefined, 'Operations'),
      event('submitted', 'Application submitted', daysAgo(9)),
    ],
  },

  /* ---- Approved, already on the roster ---------------------------------- */
  {
    id: 'app_2010',
    phone: '9911223344',
    status: 'approved',
    submittedAt: daysAgo(24),
    updatedAt: daysAgo(16),
    professionalId: 'pro_meera',
    personal: {
      firstName: 'Meera',
      lastName: 'Iyer',
      dateOfBirth: '1995-05-08',
      gender: 'female',
      email: 'meera.iyer@example.com',
      addressLine1: 'Lake View, 305',
      addressLine2: 'Powai',
      city: 'Mumbai',
      state: 'Maharashtra',
      pincode: '400076',
      emergencyContactName: 'Shanti Iyer',
      emergencyContactPhone: '9911220000',
    },
    professional: {
      categoryIds: ['cat_makeup'],
      specialties: ['Bridal', 'Engagement'],
      experienceYears: 5,
      bio: 'Specialises in modern engagement and bridal looks.',
      travelRadiusKm: 15,
      languages: ['English', 'Hindi', 'Tamil'],
    },
    bank: {
      accountHolderName: 'Meera Iyer',
      accountNumberLast4: '5567',
      ifsc: 'HDFC0000112',
      bankName: 'HDFC Bank',
    },
    documents: docs({
      profile_photo: { state: 'verified' },
      id_proof: { state: 'verified' },
      address_proof: { state: 'verified' },
      certificate: { state: 'verified' },
      police_verification: { state: 'verified' },
    }),
    outcome: {
      attended: true,
      skillScore: 4,
      conductScore: 5,
      notes: 'Solid bridal portfolio, warm manner. Approved.',
      recordedBy: 'Operations',
      recordedAt: daysAgo(17),
    },
    timeline: [
      event('approved', 'Approved and added to roster', daysAgo(16), undefined, 'Operations'),
      event('interview_done', 'Interview completed', daysAgo(17), undefined, 'Operations'),
      event('submitted', 'Application submitted', daysAgo(24)),
    ],
  },

  /* ---- Declined -------------------------------------------------------- */
  {
    id: 'app_1998',
    phone: '9088990022',
    status: 'rejected',
    submittedAt: daysAgo(31),
    updatedAt: daysAgo(27),
    personal: {
      firstName: 'Anil',
      lastName: 'Kumar',
      dateOfBirth: '2001-09-30',
      gender: 'male',
      email: 'anil.kumar@example.com',
      addressLine1: 'Shanti Nivas, 2',
      city: 'Nagpur',
      state: 'Maharashtra',
      pincode: '440010',
      emergencyContactName: 'Sunita Kumar',
      emergencyContactPhone: '9088990011',
    },
    professional: {
      categoryIds: ['cat_hair'],
      specialties: ["Men's Grooming"],
      experienceYears: 1,
      bio: 'Recently completed a barbering course, keen to build experience.',
      travelRadiusKm: 10,
      languages: ['Hindi', 'Marathi'],
    },
    documents: docs({
      profile_photo: { state: 'verified' },
      id_proof: { state: 'verified' },
      address_proof: { state: 'verified' },
      certificate: { state: 'missing', fileName: undefined, uploadedAt: undefined },
      police_verification: { state: 'missing', fileName: undefined, uploadedAt: undefined },
    }),
    decisionReason:
      'We are not currently operating in Nagpur, and the categories applied for need at least 2 years of professional experience.',
    reapplyAfter: daysAhead(3),
    timeline: [
      event('rejected', 'Application declined', daysAgo(27), 'Outside service area', 'Operations'),
      event('under_review', 'Moved to review', daysAgo(30), undefined, 'Operations'),
      event('submitted', 'Application submitted', daysAgo(31)),
    ],
  },
];

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

export function applicantName(app: PartnerApplication): string {
  return `${app.personal.firstName} ${app.personal.lastName}`.trim();
}

/** Photo to show in lists. Falls back to a deterministic portrait. */
export function applicantPhoto(app: PartnerApplication): string {
  const photo = app.documents.find((d) => d.kind === 'profile_photo');
  return photo?.uri ?? portraitForId(app.id);
}

/**
 * Whether every required document has been verified.
 *
 * This is the gate on approval: an applicant cannot be activated while a
 * mandatory document is unverified, because that document is the only thing
 * standing between us and sending an unvetted stranger into a customer's home.
 */
export function documentsReady(app: PartnerApplication): boolean {
  return REQUIRED_DOCUMENTS.every(
    (kind) => app.documents.find((d) => d.kind === kind)?.state === 'verified',
  );
}

export function pendingDocuments(app: PartnerApplication): DocumentKind[] {
  return REQUIRED_DOCUMENTS.filter(
    (kind) => app.documents.find((d) => d.kind === kind)?.state !== 'verified',
  );
}

/** Working days an application has been open. Drives the ageing warning. */
export function daysOpen(app: PartnerApplication): number {
  if (!app.submittedAt) return 0;
  return Math.floor((Date.now() - new Date(app.submittedAt).getTime()) / 86_400_000);
}

/** Applications sitting longer than this need chasing. */
export const SLA_DAYS = 2;

export function isOverdue(app: PartnerApplication): boolean {
  return isOpen(app.status) && daysOpen(app) > SLA_DAYS;
}
